import { getLocalContextualDateRange, getLocalStandardDate } from '@tesla/design-system';
import { iconCalendar } from '@tesla/design-system-icons';
import cx from 'classnames';
import React, { useEffect } from 'react';
import { DateRangePicker, } from '../date-picker/date-range-picker.js';
import { getDatePickerDate } from '../date-picker/index.js';
import { Tooltip, useTooltipState } from '../tooltip/index.js';
import { Icon } from '../icon/index.js';
import { IconButton } from '../icon-button/index.js';
import { FormFeedback } from '../form-feedback/index.js';
import { FormInputText } from '../form-input-text/index.js';
import { FormItem as TdsFormItem } from '../form-item/index.js';
import { FormLabel } from '../form-label/index.js';
const FormItem = ({ density, disabled, error, feedbackProps, inputProps, labelProps, }) => (React.createElement(TdsFormItem, { className: cx('tds-flex-item'), density: density, disabled: disabled, error: error },
    React.createElement(FormLabel, { ...labelProps }),
    React.createElement(FormInputText, { ...inputProps }),
    !!error && (React.createElement(FormFeedback, { className: "tds-form-feedback-file", feedbackText: feedbackProps.feedbackText, helperText: feedbackProps.helperText })),
    !error && (feedbackProps.feedbackText || feedbackProps.helperText) && (React.createElement(FormFeedback, { className: "tds-form-feedback-file", feedbackText: feedbackProps.feedbackText, helperText: feedbackProps.helperText }))));
export const FormItemDateRange = ({ className, dateDisabled, dateType, dateRangePickerProps, density, disabled, end, endInputProps = {}, endInputRef, endLabelProps = {
    children: 'End Date',
}, error, feedback, feedbackText, helper, helperText, layout, locale = 'en-US', onBlur, onDateChange, open, selectOnFocus, readOnly, singleInput = false, singleInputInputProps = {}, singleInputLabelProps = {
    children: 'Dates',
}, singleInputRef, start, startInputProps = {}, startInputRef, startLabelProps = {
    children: 'Start Date',
}, tooltipProps, ...rest }) => {
    const { expanded, handlers, setExpanded, wrapperRef } = useTooltipState();
    const startDate = start ? getDatePickerDate(start, dateType) : null;
    const endDate = end ? getDatePickerDate(end, dateType) : null;
    // Assign text input values here
    // Use the prop if provided, else
    // Use the date formatters from TDS, else
    // Use empty strings
    // NB: single input will display the start date by itself if no end date selected
    let startInputValue;
    if (startInputProps.value) {
        startInputValue = startInputProps.value;
    }
    else if (startDate) {
        startInputValue = getLocalStandardDate(startDate, locale);
    }
    else {
        startInputValue = '';
    }
    let endInputValue;
    if (endInputProps.value) {
        endInputValue = endInputProps.value;
    }
    else if (endDate) {
        endInputValue = getLocalStandardDate(endDate, locale);
    }
    else {
        endInputValue = '';
    }
    let singleInputValue;
    if (singleInputInputProps.value) {
        singleInputValue = singleInputInputProps.value;
    }
    else if (startDate && !endDate) {
        singleInputValue = getLocalStandardDate(startDate, locale);
    }
    else if (startDate && endDate) {
        singleInputValue = getLocalContextualDateRange(startDate, endDate, locale);
    }
    else {
        singleInputValue = '';
    }
    // Close on user or parent input
    useEffect(() => {
        if (start && end) {
            setExpanded(false);
        }
    }, [start, end, setExpanded]);
    const handleKeyDown = (e) => {
        // Prevent accidental scrolling of the page
        if (e.key === ' ')
            e.preventDefault();
        switch (e.key) {
            case ' ':
            case 'ArrowDown':
            case 'Enter':
            case 'Return':
                setExpanded(true);
                break;
            default:
                break;
        }
    };
    // onBlur for the component should only fire when the user is leaving the component
    // since onBlurs can happen as the user clicks around within the component,
    // we have to handle the behavior explicitly
    // @ts-expect-error - React's event types are not self-compatible in this case
    const handleBlur = (e) => {
        // Note: related target represents the item focused-to in the context of a blur event
        if (wrapperRef.current && !wrapperRef.current.contains(e.relatedTarget)) {
            if (onBlur)
                onBlur(e);
        }
    };
    // Props shared between all three expressions of the text inputs
    const commonInputProps = {
        disabled,
        readOnly,
        selectOnFocus,
        externallyControlled: true,
        onClick: handlers.onClick,
        onKeyDown: handleKeyDown,
        trailing: !readOnly ? (React.createElement(IconButton, { onClick: handlers.onClick, disabled: disabled },
            React.createElement(Icon, { data: iconCalendar, inline: true }))) : undefined,
    };
    const itemProps = {
        density,
        disabled,
        error,
        feedbackProps: {
            feedbackText: feedbackText !== null && feedbackText !== void 0 ? feedbackText : feedback,
            helperText: helperText !== null && helperText !== void 0 ? helperText : helper,
        },
    };
    return (React.createElement("div", { className: cx('tds-date-picker--range', { 'tds-date-picker--horizontal': layout === 'horizontal' }, className), onBlur: handleBlur, onKeyDown: handlers.onKeyDown, ref: wrapperRef, ...rest },
        singleInput ? (React.createElement(FormItem, { ...itemProps, 
            // @ts-expect-error - React's event types are not self-compatible in this case
            inputProps: {
                ...singleInputInputProps,
                ...commonInputProps,
                value: singleInputValue,
                inputRef: singleInputRef,
            }, labelProps: singleInputLabelProps })) : (React.createElement(React.Fragment, null,
            React.createElement(FormItem, { ...itemProps, 
                // @ts-expect-error - React's event types are not self-compatible in this case
                inputProps: {
                    ...startInputProps,
                    ...commonInputProps,
                    value: startInputValue,
                    inputRef: startInputRef,
                }, labelProps: startLabelProps }),
            React.createElement(FormItem, { ...itemProps, 
                // @ts-expect-error - React's event types are not self-compatible in this case
                inputProps: {
                    ...endInputProps,
                    ...commonInputProps,
                    value: endInputValue,
                    inputRef: endInputRef,
                }, labelProps: endLabelProps }))),
        !readOnly && (React.createElement(Tooltip, { className: "tds-date-range-picker-tooltip", open: open !== null && open !== void 0 ? open : expanded, orientation: "down", ...tooltipProps },
            React.createElement(DateRangePicker, { dateType: dateType, density: density, disabled: dateDisabled, end: end, layout: layout, locale: locale, onDateChange: onDateChange, start: start, ...dateRangePickerProps })))));
};
